import React, { useEffect, useState } from "react";
import { Breadcrumb, Layout, Tabs, theme, Spin, Card, Row, Col, Typography, Tag, Button, Space } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { useParams, useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { PlanLevelEnum } from "../../enums/PlanLevelEnum";
import { IOrganisation } from "../../services/nestjs-base-api/types";
import { useGetAdminOrganisationByIdQuery } from "../../services/nestjs-base-api/admin-api";
import { ArrowLeftOutlined, TeamOutlined, RocketOutlined, CalendarOutlined, EditOutlined } from "@ant-design/icons";

const { Content } = Layout;
const { Title, Text } = Typography;

const AdminOrganisationDetailPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const { organisationId } = useParams<{ organisationId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("details");
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // Fetch organisation data
  const { data, error, isLoading } = useGetAdminOrganisationByIdQuery({ 
    jwtToken: user.jwtToken,
    id: organisationId ? parseInt(organisationId) : 0
  }, { pollingInterval: 600000 });

  useEffect(() => {
    if (error) {
      if (error && 'status' in error) {
        if (error.status === 401) {
          logout();
        }
      }
    }
  }, [error, logout]);

  // Extract organisation data safely
  const organisation = data as unknown as IOrganisation;

  // Function to get plan name based on enum or string value
  const getPlanName = (plan: PlanLevelEnum | string): string => {
    // Handle string values like "PLAN_1" directly from API
    const planValue = typeof plan === 'string' ? plan : String(plan);

    switch (planValue) {
      case PlanLevelEnum.Plan1:
      case "PLAN_1":
        return intl.formatMessage({ id: 'organisation.plan.basic' }) || 'Basic';
      case PlanLevelEnum.Plan2:
      case "PLAN_2":
        return intl.formatMessage({ id: 'organisation.plan.standard' }) || 'Standard';
      case PlanLevelEnum.Plan3:
      case "PLAN_3":
        return intl.formatMessage({ id: 'organisation.plan.professional' }) || 'Professional';
      case PlanLevelEnum.Plan4:
      case "PLAN_4":
        return intl.formatMessage({ id: 'organisation.plan.enterprise' }) || 'Enterprise';
      default:
        return String(plan) || 'Basic';
    }
  };

  // Function to get plan color based on enum or string value
  const getPlanColor = (plan: PlanLevelEnum | string): string => {
    // Handle string values like "PLAN_1" directly from API
    const planValue = typeof plan === 'string' ? plan : String(plan);

    switch (planValue) {
      case PlanLevelEnum.Plan1:
      case "PLAN_1":
        return 'blue';
      case PlanLevelEnum.Plan2:
      case "PLAN_2":
        return 'cyan';
      case PlanLevelEnum.Plan3:
      case "PLAN_3":
        return 'purple';
      case PlanLevelEnum.Plan4:
      case "PLAN_4":
        return 'gold';
      default:
        return 'default';
    }
  };

  const handleEditOrganisation = () => {
    navigate(`/admin/organisations/${organisationId}/edit`);
  };

  // Handle tab change
  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  if (isLoading) {
    return (
      <Content style={{ margin: '0 16px' }}>
        <Breadcrumb style={{ margin: '16px 0' }}>
          <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
          <Breadcrumb.Item>
            <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
          </Breadcrumb.Item>
          <Breadcrumb.Item onClick={() => navigate('/admin/organisations')} className="breadcrumb-link">
            <FormattedMessage id={'section.organisationsName'} defaultMessage="Organisations" />
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <FormattedMessage id={'admin.organisation.details'} defaultMessage="Organisation Details" />
          </Breadcrumb.Item>
        </Breadcrumb>
        <div
          style={{
            padding: 24,
            minHeight: '79vh',
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spin size="large" />
        </div>
      </Content>
    );
  }

  return (
    <Content style={{ margin: '0 16px' }}>
      <Breadcrumb style={{ margin: '16px 0' }}>
        <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
        </Breadcrumb.Item>
        <Breadcrumb.Item onClick={() => navigate('/admin/organisations')} className="breadcrumb-link">
          <FormattedMessage id={'section.organisationsName'} defaultMessage="Organisations" />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'admin.organisation.details'} defaultMessage="Organisation Details" />
        </Breadcrumb.Item>
      </Breadcrumb>
      <div
        style={{
          padding: 24,
          minHeight: '79vh',
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/admin/organisations')}
          >
            <FormattedMessage id={'common.back'} defaultMessage="Back to Organisations" />
          </Button>
        </div>

        <Card title={
          <Space>
            <Title level={2}>{organisation?.name}</Title>
            {organisation?.plan && (
              <Tag color={getPlanColor(organisation.plan)} style={{ marginLeft: 8 }}>
                {getPlanName(organisation.plan)}
              </Tag>
            )}
          </Space>
        } 
        extra={
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={handleEditOrganisation}
          >
            <FormattedMessage id={'common.edit'} defaultMessage="Edit" />
          </Button>
        }>
          <Tabs activeKey={activeTab} onChange={handleTabChange}>
            <Tabs.TabPane 
              tab={
                <span>
                  <TeamOutlined />
                  <FormattedMessage id={'organisation.tabs.details'} defaultMessage="Details" />
                </span>
              } 
              key="details"
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Card title={<FormattedMessage id={'organisation.basicInfo'} defaultMessage="Basic Information" />}>
                    <Row>
                      <Col span={8}><Text strong><FormattedMessage id={'organisation.id'} defaultMessage="ID:" /></Text></Col>
                      <Col span={16}>{organisation?.id}</Col>
                    </Row>
                    <Row style={{ marginTop: 16 }}>
                      <Col span={8}><Text strong><FormattedMessage id={'organisation.name'} defaultMessage="Name:" /></Text></Col>
                      <Col span={16}>{organisation?.name}</Col>
                    </Row>
                    <Row style={{ marginTop: 16 }}>
                      <Col span={8}><Text strong><FormattedMessage id={'organisation.plan'} defaultMessage="Plan:" /></Text></Col>
                      <Col span={16}>
                        <Tag color={getPlanColor(organisation?.plan || PlanLevelEnum.Plan1)}>
                          {getPlanName(organisation?.plan || PlanLevelEnum.Plan1)}
                        </Tag>
                      </Col>
                    </Row>
                    {/* Add more organisation details here as needed */}
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card title={<FormattedMessage id={'organisation.stats'} defaultMessage="Statistics" />}>
                    <Row>
                      <Col span={8}><Text strong><FormattedMessage id={'organisation.userCount'} defaultMessage="Total Users:" /></Text></Col>
                      <Col span={16}>-</Col>
                    </Row>
                    <Row style={{ marginTop: 16 }}>
                      <Col span={8}><Text strong><FormattedMessage id={'organisation.eventCount'} defaultMessage="Total Events:" /></Text></Col>
                      <Col span={16}>-</Col>
                    </Row>
                    <Row style={{ marginTop: 16 }}>
                      <Col span={8}><Text strong><FormattedMessage id={'organisation.created'} defaultMessage="Created:" /></Text></Col>
                      <Col span={16}>-</Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </Tabs.TabPane>
            
            <Tabs.TabPane 
              tab={
                <span>
                  <TeamOutlined />
                  <FormattedMessage id={'organisation.tabs.users'} defaultMessage="Users" />
                </span>
              } 
              key="users"
            >
              <Button 
                type="primary" 
                onClick={() => navigate(`/admin/organisations/${organisationId}/users`)}
              >
                <FormattedMessage id={'admin.organisation.manageUsers'} defaultMessage="Manage Users" />
              </Button>
            </Tabs.TabPane>
            
            <Tabs.TabPane 
              tab={
                <span>
                  <CalendarOutlined />
                  <FormattedMessage id={'organisation.tabs.events'} defaultMessage="Events" />
                </span>
              } 
              key="events"
            >
              <Button 
                type="primary" 
                onClick={() => navigate(`/admin/organisations/${organisationId}/events`)}
              >
                <FormattedMessage id={'admin.organisation.viewEvents'} defaultMessage="View Events" />
              </Button>
            </Tabs.TabPane>
            
            <Tabs.TabPane 
              tab={
                <span>
                  <RocketOutlined />
                  <FormattedMessage id={'organisation.tabs.features'} defaultMessage="Features" />
                </span>
              } 
              key="features"
            >
              <Button 
                type="primary" 
                onClick={() => navigate(`/admin/organisations/${organisationId}/features`)}
              >
                <FormattedMessage id={'admin.organisation.manageFeatures'} defaultMessage="Manage Features" />
              </Button>
            </Tabs.TabPane>
          </Tabs>
        </Card>
      </div>
    </Content>
  );
};

export default AdminOrganisationDetailPage;